'use client';

import React from 'react';
import type {
  EvidenceReasoning,
  EvidenceConfidence,
  EvidenceReference,
  WidgetEvidence,
} from 'src/api/redux/profileConfiguration/types';

const confidenceConfig: Record<
  EvidenceConfidence,
  { label: string; className: string }
> = {
  ultra: { label: 'Ultra', className: 'bg-green-100 text-green-800' },
  high: { label: 'High', className: 'bg-sky-100 text-sky-800' },
  low: { label: 'Low', className: 'bg-amber-100 text-amber-800' },
};

const ConfidenceBadge: React.FC<{ confidence: EvidenceConfidence }> = ({
  confidence,
}) => {
  const config = confidenceConfig[confidence] ?? confidenceConfig.low;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
      data-testid="evidence-confidence-badge"
    >
      {config.label}
    </span>
  );
};

const ReferenceItem: React.FC<{ reference: EvidenceReference }> = ({
  reference,
}) => (
  <div
    className="flex flex-col gap-0.5"
    data-testid={`evidence-reference-${reference.type}`}
  >
    {reference.source_text && (
      <p className="text-xs text-neutral-600 italic leading-snug">
        &ldquo;{reference.source_text}&rdquo;
      </p>
    )}
    <p className="text-xs text-neutral-500 leading-snug">
      {reference.relevance}
    </p>
  </div>
);

interface EvidenceTooltipContentProps {
  evidence: EvidenceReasoning | WidgetEvidence;
}

const isWidgetEvidence = (
  e: EvidenceReasoning | WidgetEvidence
): e is WidgetEvidence => 'parameter_rationale' in e;

const EvidenceTooltipContent: React.FC<EvidenceTooltipContentProps> = ({
  evidence,
}) => (
  <div
    className="flex flex-col gap-2 max-w-80"
    data-testid="evidence-tooltip-content"
  >
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-neutral-900 uppercase tracking-wide">
        Confidence
      </span>
      <ConfidenceBadge confidence={evidence.confidence} />
    </div>

    <p className="text-xs text-neutral-600 leading-snug">
      {evidence.confidence_rationale}
    </p>

    {evidence.references.length > 0 && (
      <div className="flex flex-col gap-1.5 border-t border-neutral-100 pt-2">
        {evidence.references.map((ref, idx) => (
          <ReferenceItem key={idx} reference={ref} />
        ))}
      </div>
    )}

    {isWidgetEvidence(evidence) &&
      Object.keys(evidence.parameter_rationale).length > 0 && (
        <div className="flex flex-col gap-1 border-t border-neutral-100 pt-2">
          {Object.entries(evidence.parameter_rationale).map(
            ([param, rationale]) => (
              <div key={param} className="flex flex-col">
                <span className="text-xs font-medium text-neutral-700">
                  {param.replace(/_/g, ' ')}
                </span>
                <p className="text-xs text-neutral-500 leading-snug">
                  {rationale}
                </p>
              </div>
            )
          )}
        </div>
      )}
  </div>
);

export default EvidenceTooltipContent;
